/**
 * Audit / member-ledger API wrappers.
 *
 * `GET /v2/admin/audit` is the business-action feed used by the global Activity log.
 * `GET /v2/admin/members/{contactId}/ledger` is the separate member credit ledger
 * used by the member detail ledger/activity views. Both are bearer-only v2 admin
 * surfaces through `request()`.
 */
import { request } from './client';
import { toWirePage, toUiPage } from './paging';

/** Feed entry kind -> drives the colored icon bubble. */
export type ActivityKind = 'purchase' | 'credit' | 'membership' | 'reservation';
/** Actor classification (DNKAPI-0076). */
type ActorType = 'member' | 'guest' | 'admin' | 'system';
/** Session-mode attribution on an ADMIN actor (DNKAPI-0088). */
export type SessionMode = 'front-desk' | 'open';
/** Whether a purchase was a one-time charge or a recurring charge. */
type PurchaseType = 'one_time' | 'recurring';

interface ActivityActor {
  type: ActorType | null;
  id: string | null;
  email: string | null;
  name: string | null;
  sessionMode: SessionMode | null;
  /**
   * The STRUCTURED platform-support marker (DNKAPI-0513): `'PLATFORM_SUPPORT'` on a row
   * written by a platform-support session, `null` on every other row — including every row
   * from a server that predates the field. Matched BY VALUE, never by presence (the same
   * rule DNKADM-0356 applies to the token's `principal_type` claim), and NEVER derived from
   * the ` (platform support)` text in `name`, which stays verbatim display text
   * (DNKADM-0357). Display-only: nothing gates on it.
   */
  principalType: 'PLATFORM_SUPPORT' | null;
}

interface ActivityTarget {
  id: string | null;
  contactId: string | null;
  name: string | null;
  email: string | null;
}

export interface ActivityEntry {
  id: string | null;
  kind: ActivityKind | null;
  action: string | null;
  detail: string | null;
  target: ActivityTarget;
  actor: ActivityActor;
  timestamp: string | null;
  reason: string | null;
  note: string | null;
  bucket: string | null;
  change: number | null;
  balance: number | null;
  amountCents: number | null;
  currency: string | null;
  plan: string | null;
  tier: string | null;
  term: string | null;
  paymentRef: string | null;
  purchaseType: PurchaseType | null;
  serviceLocationId: string | null;
  targetReservationId: string | null;
  /**
   * The reservation UUID for a reservation-related row (DNKAPI-0281 U1, best-effort
   * — `null` until the enrichment ships). Drives the `/reservations?reservation=<uuid>`
   * deep link (DNKADM-0147); the numeric `targetReservationId` is the plain-text
   * fallback when this is absent.
   */
  targetReservationUuid: string | null;
  details: Record<string, unknown> | null;
}

export interface ActivityPage {
  entries: ActivityEntry[];
  page: number;
  pageLimit: number;
  total: number | null;
}

/** Member-ledger transaction kind. `UNKNOWN` is the neutral fallback for future values. */
export type MemberLedgerKind = 'DEPOSIT' | 'DRAW' | 'REFUND' | 'EXPIRE' | 'ADJUST' | 'UNKNOWN';

export interface MemberLedgerEntry {
  transactionId: string | null;
  occurredAt: string | null;
  bucketId: string | null;
  bucketName: string | null;
  /** Signed amount: negative = draw/debit; positive = grant/deposit/refund. */
  amount: number | null;
  kind: MemberLedgerKind;
  reason?: string;
  lotId?: string;
  balanceAfter?: number;
}

export interface MemberLedgerPage {
  entries: MemberLedgerEntry[];
  page: number;
  size: number;
  totalElements: number | null;
}

export interface MemberLedgerParams {
  bucketId?: string;
  from?: string;
  to?: string;
  /**
   * The endpoint's own `kind` filter. Since DNKAPI-0687 an unknown kind is ignored (no
   * filter), not a 400; `UNKNOWN` is still never sent, it is not a wire value.
   */
  kind?: Exclude<MemberLedgerKind, 'UNKNOWN'>;
  /** 0-indexed page. */
  page?: number;
  size?: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function asId(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return asText(value);
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function pickText(o: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const k of keys) {
    const v = asText(o[k]);
    if (v !== null) return v;
  }
  return null;
}

function parseActorType(raw: unknown): ActorType | null {
  const text = asText(raw);
  if (text === null) return null;
  const t = text.toLowerCase();
  return t === 'member' || t === 'guest' || t === 'admin' || t === 'system'
    ? (t as ActorType)
    : null;
}

function parseSessionMode(raw: unknown): SessionMode | null {
  const text = asText(raw);
  if (text === null) return null;
  const m = text.toLowerCase();
  return m === 'front-desk' || m === 'open' ? (m as SessionMode) : null;
}

/**
 * The actor's structured principal type (DNKAPI-0513) — exactly `'PLATFORM_SUPPORT'` is the
 * marker; absent, `null`, or ANY other string is `null`. By value, never by presence: a
 * near-miss like `'PLATFORM'` is not a support row, and — unlike `parseActorType`/
 * `parseSessionMode` above — the value is NOT case-folded, so `'platform_support'` is `null`
 * too. Widening an exact-value match to a case-insensitive one would turn it back into a
 * by-presence test, which is what DNKADM-0358 exists to avoid.
 *
 * Deliberately NOT gated on `actorType`, though `sessionMode` two lines up is: gating would
 * make the marker depend on something other than the field, and the field alone is the
 * contract. Under DNKAPI-0513 only an `ADMIN` row can carry it, so the gate would be
 * unreachable anyway — it would only add a second, silent way to lose the marker.
 */
function parsePrincipalType(raw: unknown): 'PLATFORM_SUPPORT' | null {
  return raw === 'PLATFORM_SUPPORT' ? 'PLATFORM_SUPPORT' : null;
}

/**
 * The actor's DISPLAY NAME — the resolved label only, never a raw principal id
 * (DNKADM-0394 U2).
 *
 * Two wire keys carry identity and they are not interchangeable:
 *  - `actorLabel` is the RESOLVED display name. Since DNKAPI-0547 U1 it carries a member's
 *    or guest's real name wherever the server can resolve one; on an ADMIN/SYSTEM row it is
 *    the login. It is `null` when unresolvable — and, per that contract, it is **never** the
 *    raw principal.
 *  - `actor` is the raw PRINCIPAL beside it: a login/email on an ADMIN/SYSTEM row (the only
 *    identity those rows carried before 0547, and displayable), but an opaque contact id on
 *    a MEMBER/GUEST row.
 *
 *  - `name` is a bare legacy alias, tolerated like the alternate wire shapes elsewhere in this
 *    module. It is taken on EVERY actor type, self-service included: the key asserts its own
 *    contents — a value filed under `name` is a name, and a principal id arriving there would
 *    be a different server bug from the one this gate exists for. No captured row carries it
 *    (the 2026-08-04 staging capture has `actorLabel`/`actor`/`actorEmail` and no `name`), so
 *    it is a compatibility tail, not a live path.
 *
 * So ONLY the `actor` fallback is gated, on `selfService`. Taking it unconditionally is
 * precisely what rendered `FGWGb0iV1qP1Gp3vAOuB` as the actor on every member-app booking: the
 * console composed a display name out of an id the server had deliberately declined to resolve.
 * On a self-service row an unresolved label now stays `null`, and `actorLineLabel` says the role
 * in words ("Member app" / "Guest") instead.
 *
 * The keys are resolved in their ORIGINAL relative order — `actorLabel` → `actor_label` →
 * `actor` → `name` — with only the third step skipped on a self-service row. A non-self-service
 * row therefore resolves byte-identically to the single `pickText` chain this replaced; the one
 * behaviour change in this unit is the skip, and nothing else moved.
 */
function actorDisplayName(o: Record<string, unknown>, selfService: boolean): string | null {
  const resolved = pickText(o, ['actorLabel', 'actor_label']);
  if (resolved !== null) return resolved;
  const principal = selfService ? null : pickText(o, ['actor']);
  if (principal !== null) return principal;
  return pickText(o, ['name']);
}

function kindForAction(action: string | null): ActivityKind | null {
  if (action === null) return null;
  const a = action.toLowerCase();
  if (a.startsWith('reservation')) return 'reservation';
  if (a.startsWith('purchase')) return 'purchase';
  if (a.startsWith('membership') || a === 'membership_change') return 'membership';
  return null;
}

/**
 * `details` rides the wire as a JSON-ENCODED STRING (DNKAPI-0613) — older rows carry an
 * object, a bare token (`member_cancel`) or null. Decode defensively: anything that is not
 * (or does not decode to) a plain object degrades to `null`, so the row renders as before.
 */
function parseDetails(raw: unknown): Record<string, unknown> | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return isObject(value) && !Array.isArray(value) ? value : null;
}

function parseV2Entry(raw: unknown): ActivityEntry {
  const o = isObject(raw) ? raw : {};
  const actorType = parseActorType(o['actorType'] ?? o['actor_type']);
  const sessionMode =
    actorType === 'admin'
      ? parseSessionMode(o['actorSessionMode'] ?? o['actor_session_mode'])
      : null;
  const targetContactId = pickText(o, ['targetContactId', 'target_contact_id', 'contactId']);
  const action = pickText(o, ['action', 'event']);
  // A self-service row — the member/guest acting in the member app, as opposed to a desk
  // action. The distinction decides whether the raw `actor` PRINCIPAL is displayable; see
  // `actorDisplayName` below.
  const selfService = actorType === 'member' || actorType === 'guest';
  return {
    id: pickText(o, ['id', 'auditId', 'eventId']),
    kind: kindForAction(action),
    action,
    detail: pickText(o, ['detail', 'description']),
    target: {
      id: targetContactId,
      contactId: targetContactId,
      // DNKAPI-0281 U1 enrichment (best-effort): `targetContactName` /
      // `targetContactEmail` light up the Member column with a resolved name/email,
      // falling back to the legacy `targetName`/`targetEmail` keys then null.
      name: pickText(o, ['targetContactName', 'target_contact_name', 'targetName', 'targetLabel']),
      email: pickText(o, ['targetContactEmail', 'target_contact_email', 'targetEmail']),
    },
    actor: {
      type: actorType,
      id: pickText(o, ['actorId', 'actor_id']),
      email: pickText(o, ['actorEmail', 'actor_email', 'email']),
      name: actorDisplayName(o, selfService),
      sessionMode,
      principalType: parsePrincipalType(o['actorPrincipalType'] ?? o['actor_principal_type']),
    },
    timestamp: pickText(o, ['createdAt', 'timestamp', 'occurredAt', 'time']),
    reason: pickText(o, ['reason']),
    note: pickText(o, ['note', 'auditNote']),
    bucket: null,
    change: null,
    balance: null,
    amountCents: null,
    currency: null,
    plan: null,
    tier: null,
    term: null,
    paymentRef: null,
    purchaseType: null,
    serviceLocationId: pickText(o, ['locationId', 'location_id', 'serviceLocationId']),
    targetReservationId: pickText(o, ['targetReservationId', 'target_reservation_id']),
    targetReservationUuid: pickText(o, ['targetReservationUuid', 'target_reservation_uuid']),
    details: parseDetails(o['details']),
  };
}

function extractV2Entries(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (!isObject(body)) return [];
  // `content` is the SHIPPED paged envelope (`{content,page,size,totalElements,totalPages}`) —
  // the same shape `parseLedgerPage` already handles (DNKADM-0248; the feed rendered its
  // empty state against 35 real rows until this key was read). The rest are tolerated
  // legacy/alternate shapes kept as fallbacks.
  for (const key of ['content', 'entries', 'audit', 'items', 'results', 'data']) {
    const value = body[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function timestampOrder(ts: string | null): number {
  if (ts === null) return Number.NEGATIVE_INFINITY;
  const ms = Date.parse(ts);
  if (Number.isFinite(ms)) return ms;
  const num = Number(ts);
  return Number.isFinite(num) ? num : Number.NEGATIVE_INFINITY;
}

function sortActivityNewestFirst(entries: ActivityEntry[]): ActivityEntry[] {
  return entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) => timestampOrder(b.e.timestamp) - timestampOrder(a.e.timestamp) || a.i - b.i)
    .map(({ e }) => e);
}

function parseV2Page(body: unknown, fallbackPage: number, fallbackLimit: number): ActivityPage {
  const o = isObject(body) ? body : {};
  return {
    entries: sortActivityNewestFirst(extractV2Entries(body).map(parseV2Entry)),
    page: asNumber(o['page']) ?? fallbackPage,
    pageLimit: asNumber(o['size']) ?? asNumber(o['pageLimit']) ?? fallbackLimit,
    // The envelope's count is `totalElements` (DNKADM-0248) — the legacy `total` stays a
    // fallback. Mirrors `parseLedgerPage`. Stays `number | null`, NEVER coerced to 0: a 0
    // makes the pager compute a last page of 1 and wrongly disable Next on a populated feed.
    total: asNumber(o['totalElements']) ?? asNumber(o['total']),
  };
}

/**
 * The `/v2/admin/audit` actions we carry FRIENDLY COPY for. Exact strings — the
 * backend filters on `?kind=<action>` (an exact single match). Purchase/Dues are
 * money events → the Transaction Ledger, NOT this action-history feed, so they are
 * absent here.
 *
 * This is the LABEL key set, no longer the filter vocabulary: since DNKADM-0259 the
 * Type filter is built from {@link listActivityKinds}, so a value outside this union
 * is still offered and filterable — it just renders a humanized label.
 *
 * The check-in action is spelled **`reservation_check_in`**. dink-api confirmed
 * database-wide on 2026-08-04 (`SELECT DISTINCT action FROM v2_activity_audit`, no
 * tenant filter) that `reservation_checkin` has never been emitted in ANY tenant, so
 * there is deliberately no alias for the old spelling (DNKADM-0259, absorbed
 * DNKADM-0258 — a shim for a string nothing emits would outlive the memory of why).
 */
type ActivityAction =
  // Reservations
  | 'reservation_create'
  | 'reservation_reschedule'
  | 'reservation_cancel'
  | 'reservation_check_in'
  | 'reservation_no_show'
  | 'reservation_confirm'
  | 'reservation_action_refused'
  | 'reservation_reject'
  // Member admin
  | 'member_added'
  | 'membership_set'
  | 'member_guest_status_set'
  | 'profile_values_set'
  | 'coach_assign'
  | 'coach_clear'
  | 'photo_set'
  | 'photo_delete'
  | 'credit_adjust'
  | 'lot_expiry_change'
  | 'lot_reduce'
  // Member self
  | 'email_change'
  // Money-adjacent
  | 'no_show_charge'
  | 'package_granted'
  // Orders (the order number rides in `reason`, DNKADM-0448)
  | 'order_card'
  | 'order_external'
  | 'order_comp'
  | 'order_refund'
  | 'order_void'
  | 'comp_pin_failed'
  | 'comp_pin_locked';

/** Friendly label per `kind` — shared by the Type filter and the What-column title map. */
export const ACTIVITY_ACTION_LABELS: Record<ActivityAction, string> = {
  reservation_create: 'Booked',
  reservation_reschedule: 'Rescheduled',
  reservation_cancel: 'Cancelled',
  reservation_check_in: 'Checked in',
  reservation_no_show: 'No-show',
  reservation_confirm: 'Confirmed',
  reservation_action_refused: 'Refused actions',
  reservation_reject: 'Request declined',
  member_added: 'Member added',
  membership_set: 'Membership changed',
  member_guest_status_set: 'Guest status set',
  profile_values_set: 'Profile updated',
  coach_assign: 'Coach assigned',
  coach_clear: 'Coach cleared',
  photo_set: 'Photo set',
  photo_delete: 'Photo deleted',
  credit_adjust: 'Credit adjusted',
  lot_expiry_change: 'Credit expiry changed',
  lot_reduce: 'Credit reduced',
  email_change: 'Email changed',
  no_show_charge: 'No-show fee charged',
  package_granted: 'Package granted',
  order_card: 'Card order',
  order_external: 'Order paid at the desk',
  order_comp: 'Order comped',
  order_refund: 'Order refunded',
  order_void: 'Order voided',
  comp_pin_failed: 'Comp refused at the manager PIN',
  comp_pin_locked: 'Comps locked after wrong PINs',
};

/** One filterable activity kind from `GET /v2/admin/audit/kinds` (DNKAPI-0411). */
export interface ActivityKindOption {
  value: string;
  /** Server-declared domain group. Unknown values are tolerated and bucket to "Other". */
  category: string;
}

/** Read the kind array out of the published `{ kinds: [...] }` envelope (or a bare array). */
function extractKindEntries(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (!isObject(body)) return [];
  const kinds = body['kinds'];
  return Array.isArray(kinds) ? kinds : [];
}

/**
 * The filterable activity vocabulary — `GET /v2/admin/audit/kinds` (DNKAPI-0411,
 * DNKADM-0259). The published contract is `{ kinds: [{ value, category }] }`, sorted
 * by `value`, tenant-invariant (it comes from dink-api's code, never tenant config or
 * data) and changes only on deploy. Every value it returns is guaranteed to be accepted
 * by `?kind=<value>`, which is the property the self-building Type filter rests on — so
 * the dropdown can never drift from what the feed emits.
 *
 * Defensive like the rest of this module: a bare array is tolerated (but NOT `content`
 * — this route is not a paged envelope), a malformed entry (missing/blank `value`) is
 * DROPPED rather than throwing (one bad row must not cost the whole dropdown), a missing
 * `category` degrades to `''` so the caller buckets it under "Other", and an
 * unrecognized body yields `[]` so the screen falls back to its static list. Server
 * order is preserved.
 */
export async function listActivityKinds(): Promise<ActivityKindOption[]> {
  const body = await request<unknown>('GET', '/v2/admin/audit/kinds');
  const out: ActivityKindOption[] = [];
  for (const raw of extractKindEntries(body)) {
    const o = isObject(raw) ? raw : {};
    const value = asText(o['value']);
    if (value === null) continue;
    out.push({ value, category: asText(o['category']) ?? '' });
  }
  return out;
}

/**
 * The Type-filter value: ANY `kind` the vocabulary route returns — deliberately not the
 * closed {@link ActivityAction} label union (DNKADM-0259), so a newly-emitted action is
 * filterable the moment `/v2/admin/audit/kinds` lists it. `'all'` remains the sentinel
 * meaning "no filter".
 */
export type ActivityActionFilter = string;

interface ActivityAuditFilters {
  /** The exact `kind` to match (single action); `'all'`/undefined omits the param. */
  kind?: ActivityActionFilter;
  contactId?: string;
  /** Inclusive lower date bound, `yyyy-MM-dd`. */
  fromDate?: string;
  /** Inclusive upper date bound, `yyyy-MM-dd`. */
  toDate?: string;
  /**
   * Owned `v2_location.id` to scope the feed to (DNKAPI-0281 U1); sent only when set —
   * omitted widens back to the tenant-wide default (DNKADM-0152).
   */
  locationId?: string;
}

export interface ActivityAuditParams {
  page?: number;
  size?: number;
  filters?: ActivityAuditFilters;
}

const DEFAULT_ACTIVITY_PAGE = 1;
const DEFAULT_ACTIVITY_SIZE = 20;
const DEFAULT_LEDGER_PAGE = 0;
const DEFAULT_LEDGER_SIZE = 50;

function actionQuery(kind: ActivityActionFilter | undefined): string | undefined {
  return kind === undefined || kind === 'all' ? undefined : kind;
}

export async function listActivityAudit(
  params: ActivityAuditParams = {},
): Promise<ActivityPage> {
  const page = params.page ?? DEFAULT_ACTIVITY_PAGE;
  const size = params.size ?? DEFAULT_ACTIVITY_SIZE;
  const f = params.filters ?? {};
  // The UI keeps a 1-indexed page, but `/v2/admin/audit` is 0-indexed. Send
  // `toWirePage(page)` so the first UI page (1) maps to `?page=0` — sending
  // `?page=1` raw skipped past 0-indexed page 0 and surfaced an empty feed with
  // a non-zero total (DNKADM-0148).
  // The backend filters on `?kind=<action>` (an EXACT single match) — sending
  // `?action=` was silently ignored (DNKADM-0151). `?locationId=` scopes the feed to
  // one owned location (DNKAPI-0281 U1), sent ONLY when set — omitting it widens back
  // to the tenant-wide default (DNKADM-0152).
  const body = await request<unknown>('GET', '/v2/admin/audit', {
    query: {
      page: toWirePage(page),
      size,
      kind: actionQuery(f.kind),
      contactId: asText(f.contactId) ?? undefined,
      fromDate: asText(f.fromDate) ?? undefined,
      toDate: asText(f.toDate) ?? undefined,
      locationId: asText(f.locationId) ?? undefined,
    },
  });
  // `parseV2Page` returns the backend's raw 0-indexed page (it is shared with the
  // 0-indexed ledger, so it must not remap). Map it back to the 1-indexed UI page
  // HERE (mirrors listMembers): fall back to the 0-indexed wire page so `toUiPage`
  // round-trips the requested page when the backend omits `page`.
  const parsed = parseV2Page(body, toWirePage(page), size);
  return { ...parsed, page: toUiPage(parsed.page) };
}

function parseLedgerKind(raw: unknown): MemberLedgerKind {
  const text = asText(raw);
  if (text === null) return 'UNKNOWN';
  const k = text.toUpperCase();
  return k === 'DEPOSIT' || k === 'DRAW' || k === 'REFUND' || k === 'EXPIRE' || k === 'ADJUST'
    ? (k as MemberLedgerKind)
    : 'UNKNOWN';
}

function parseLedgerEntry(raw: unknown): MemberLedgerEntry {
  const o = isObject(raw) ? raw : {};
  const entry: MemberLedgerEntry = {
    transactionId: asId(o['transactionId'] ?? o['id']),
    occurredAt: pickText(o, ['occurredAt', 'createdAt', 'timestamp']),
    bucketId: asId(o['bucketId']),
    bucketName: pickText(o, ['bucketName', 'bucket']),
    amount: asNumber(o['amount']),
    kind: parseLedgerKind(o['kind']),
  };
  const reason = asText(o['reason']);
  if (reason !== null) entry.reason = reason;
  const lotId = asId(o['lotId']);
  if (lotId !== null) entry.lotId = lotId;
  const balanceAfter = asNumber(o['balanceAfter']);
  if (balanceAfter !== null) entry.balanceAfter = balanceAfter;
  return entry;
}

function ledgerTimestampOrder(entry: MemberLedgerEntry): number {
  return timestampOrder(entry.occurredAt);
}

function parseLedgerPage(
  body: unknown,
  fallbackPage: number,
  fallbackSize: number,
): MemberLedgerPage {
  const o = isObject(body) ? body : {};
  const raw = Array.isArray(o['content']) ? o['content'] : Array.isArray(body) ? body : [];
  const entries = raw
    .map(parseLedgerEntry)
    .map((e, i) => ({ e, i }))
    .sort((a, b) => ledgerTimestampOrder(b.e) - ledgerTimestampOrder(a.e) || a.i - b.i)
    .map(({ e }) => e);
  return {
    entries,
    page: asNumber(o['page']) ?? fallbackPage,
    size: asNumber(o['size']) ?? fallbackSize,
    totalElements: asNumber(o['totalElements']) ?? asNumber(o['total']),
  };
}

export async function getMemberLedger(
  contactId: string,
  params: MemberLedgerParams = {},
): Promise<MemberLedgerPage> {
  // Intentionally 0-indexed-UI throughout: 0-indexed in, 0-indexed wire — do NOT
  // apply toWirePage/toUiPage here (unlike the 1-indexed lists). See src/api/paging.ts.
  const page = params.page ?? DEFAULT_LEDGER_PAGE;
  const size = params.size ?? DEFAULT_LEDGER_SIZE;
  const body = await request<unknown>(
    'GET',
    `/v2/admin/members/${encodeURIComponent(contactId)}/ledger`,
    {
      query: {
        bucketId: asText(params.bucketId) ?? undefined,
        from: asText(params.from) ?? undefined,
        to: asText(params.to) ?? undefined,
        kind: params.kind,
        page,
        size,
      },
    },
  );
  return parseLedgerPage(body, page, size);
}
